// Mirrors the mgmt-api contract: effective_date is required for individual laser
// assignments (COM-000002) and constrained to the policy's effective date range.

export type LaserType = "individual" | "condition_based";

export interface LaseredClaimantEffectiveDateInput {
  laserType: LaserType | string;
  effectiveDate: string | null | undefined;
  policyStart: string | null | undefined;
  policyEnd: string | null | undefined;
}

const startOfDay = (value: string): Date => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Pure validator: returns an error message, or an empty string when valid.
 * - Required when laserType === "individual" (matches API COM-000002).
 * - When provided, must fall within [policyStart, policyEnd] inclusive.
 */
export const validateLaseredEffectiveDate = ({
  laserType,
  effectiveDate,
  policyStart,
  policyEnd,
}: LaseredClaimantEffectiveDateInput): string => {
  const isIndividual = laserType === "individual";

  if (!effectiveDate) {
    return isIndividual ? "Effective date is required for individual laser assignments" : "";
  }

  const value = startOfDay(effectiveDate);
  if (Number.isNaN(value.getTime())) {
    return "Effective date is invalid";
  }

  if (policyStart) {
    const min = startOfDay(policyStart);
    if (value < min) {
      return `Effective date must be on or after policy start (${policyStart})`;
    }
  }

  if (policyEnd) {
    const max = startOfDay(policyEnd);
    if (value > max) {
      return `Effective date must be on or before policy end (${policyEnd})`;
    }
  }

  return "";
};
